/*Copyright (c) 2025 por Tecnologías de Información MALMUR y empresas afiliadas. Todos los derechos reservados.
Este software es información confidencial y propietaria de Copyright (c) 2025 por Tecnologías de Información MALMUR
 y sus afiliadas. ("Información Confidencial"). Usted no puede divulgar dicha Información Confidencial, 
 y solo puede usar dicha Información Confidencial de acuerdo con los términos del acuerdo de licencia que celebró con MALMUR.*/

/*Copyright (c) 2025 by Tecnologías de Información MALMUR and affiliated companies. All Rights Reserved.
This software is confidential and proprietary information of Copyright (c) 2025 by Tecnologías de Información 
MALMUR and its affiliates. ("Confidential Information"). You may not disclose such Confidential Information, 
and may only use such Confidential Information in accordance with the terms of the license agreement you entered 
into with MALMUR.*/



import { Divider, Grid, Stack } from "@mui/material";
import Box from "components/generals/Box";
import Typography from "components/generals/Typography";
import { FC, useState, useEffect } from "react";
import { ProviderInterface } from "@malmur-technologies/medicalsystem-shared";
import { FormActionType } from "types/form.enum";
import FormField from "components/generals/FormInput";
import Button from "components/generals/Button";
import FormikForm from "components/layout/FormikForm";
import { Form } from "formik";
import { checkFormValidity, providerFormConfig } from "utils/form.utils";
import MedicalInformationIcon from "@mui/icons-material/MedicalInformation";
import * as Yup from "yup";
import { useTranslation } from "react-i18next";

/**
 * Props for ProviderForm component.
 */
export interface ProviderFormProps {
  model: ProviderInterface;
  closeAction?: () => void;
}

/**
 * Medical provider (doctor/nurse) information form.
 * 
 * Features:
 * - Provider personal information (name, lastname)
 * - Contact details (email, multiple phone numbers)
 * - Credentials and certifications
 * - Specialties and qualifications
 * - NPI and license information
 * - Integration with person entity
 * - Form validation with Yup schema
 * - Multi-phone number support
 * 
 * Note: Currently displays provider information for editing.
 * HTTP client integration pending (TODO marked in code).
 * 
 * @param model - Provider data to display/edit
 * @param closeAction - Optional callback when form is closed
 * @returns Provider information form component
 */
const ProviderForm: FC<ProviderFormProps> = ({ model, closeAction }) => {
  //TODO: HTTP client to store patiend data
  const { t } = useTranslation();

  const submitHandle = (provider: ProviderInterface) => {
    console.log(JSON.stringify(provider));
  };

  const formikValidationValues = Yup.object().shape(providerFormConfig);

  return (
    <FormikForm
      onSubmit={(model) => {
        submitHandle(model);
      }}
      initialValues={model}
      validationSchema={formikValidationValues}
    >
      {({ values, errors, touched, isSubmitting }) => {
        return (
          <Form id="providerData" autoComplete="off">
            <Box className="form-container" p={3} mb={4}>
              <Stack alignItems="center" direction="row" gap={2}>
                <MedicalInformationIcon fontSize="large" />
                <Typography variant="h3">Doctor Information</Typography>
              </Stack>
              <Grid container mt={2} spacing={2}>
                <Grid item xs={6}>
                  <FormField
                    type="text"
                    name="name"
                    label={t("providerForm.name")}
                    fullWidth
                    value={values.person?.name || ""}
                    error={errors.person?.name && touched.person?.name}
                  />
                </Grid>
                <Grid item xs={6}>
                  <FormField
                    type="text"
                    name="lastname"
                    label={t("providerForm.lastName")}
                    fullWidth
                    value={values.person?.lastname || ""}
                    error={errors.person?.lastname && touched.person?.lastname}
                  />
                </Grid>
              </Grid>
              <Grid container mt={2} spacing={2}>
                <Grid item xs={4}>
                  <FormField
                    type="email"
                    name="email"
                    label={t("providerForm.email")}
                    fullWidth
                    value={values.person?.user?.email || ""}
                    error={errors.person?.user?.email && touched.person?.user?.email}
                  />
                </Grid>
                <Divider />
                {values.person?.phones.map((phone, index) => (
                  <Grid key={`per.number.${index}-${phone.idx}`} item xs={12} md={6} lg={4}>
                    <FormField
                      type="tel"
                      label={`${t("providerForm.phone")} #${index + 1}`}
                      name={`person.phones[${index}].number`}
                      error={phone.number && touched.person.phones[index].number}
                      fullWidth
                    />
                  </Grid>
                ))}
                <Divider />
                <Grid item xs={4}>
                  <FormField
                    type="text"
                    name="specialty"
                    label={t("providerForm.specialty")}
                    fullWidth
                    value={values.specialty}
                    error={errors.specialty && touched.specialty}
                  />
                </Grid>
              </Grid>
              <Grid container mt={2} spacing={2}>
                <Grid item xs={6}>
                  <FormField
                    type="text"
                    name="medicalLicense"
                    label={t("providerForm.medicalLicense")}
                    fullWidth
                    value={values.medicalLicense}
                    error={errors.medicalLicense && touched.medicalLicense}
                  />
                </Grid>
                <Grid item xs={6}>
                  <FormField
                    type="text"
                    name="deaRegistration"
                    label={t("providerForm.deaRegistration")}
                    fullWidth
                    value={values.deaRegistration}
                    error={errors.deaRegistration && touched.deaRegistration}
                  />
                </Grid>
              </Grid>
              {checkFormValidity(values, errors, touched) && (
                <Box mt={2} mb={2}>
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      color: 'red',
                      textAlign: 'center'
                    }}
                  >
                    {checkFormValidity(values, errors, touched)}
                  </Typography>
                </Box>
              )}

              <Box mt={2} width="100%" display="flex" justifyContent="end">
                {closeAction && (
                  <Grid item>
                    <Button
                      variant="contained"
                      color="secondary"
                      onClick={() => {
                        closeAction();
                      }}
                    >
                      {t("providerForm.close")}
                    </Button>
                  </Grid>
                )}

                <Grid item>
                  <Button type="submit" variant="contained" color="info" disabled={isSubmitting}>
                    {t("providerForm.save")}
                  </Button>
                </Grid>
              </Box>
            </Box>
          </Form>
        );
      }}
    </FormikForm>
  );
};

export default ProviderForm;